const { Fancybox } = await import('@fancyapps/ui');

/**
 * FEATURE: Product Customiser Web Component
 *
 * A custom web component that integrates an external suit customiser iframe
 * with the Shopify product page. Handles:
 * - Opening customiser in a modal popup
 * - Loading saved configurations from URL parameters or user input
 * - Communicating with the external customiser via postMessage
 * - Converting customiser configurations into Shopify cart items
 * - Managing add-on products (logos, pockets, belt, etc.)
 */
if (!customElements.get('product-customiser')) {
  class ProductCustomiser extends HTMLElement {

    static #logoTypeMap = {
      null: 1,
      partial: 2,
      pro: 3,
    };

    static formatPrice(number, language = 'en', country = 'US') {
      return new Intl.NumberFormat(`${language}-${country}`, {
        style: "decimal",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(number);
    }

    constructor() {
      super();

      // State
      this.initalized = false;
      this.iframeOpen = false;
      this.configurationSaving = false;

      // Data
      this.configData = JSON.parse(this.dataset.config);
      this.type = this.configData.type || 'auto';

      // Selectors
      this.button = this.querySelector('button');
      this.configCodeInput = document.getElementById('configCode');

      // Get the config and customiser url
      this.originalConfigCode = this.getConfigCodeFromUrl();
      this.url = `${this.configData.url}?wait_for_initialization=true` + (this.originalConfigCode ? `&configurationCode=${this.originalConfigCode}` : '');

      // Get the edit flag
      this.edit = this.getEditFlagFromUrl();

      // Listen to window messages from the customiser
      this.customiserMessageHandler();

      // Event listeners
      this.button.addEventListener('click', (event) => {
        event.preventDefault();
        this.openCustomiser();
      });
      this.configCodeInput.addEventListener('change', (event) => {
        event.preventDefault();
        this.handleConfigCodeChanges();
      });

      // If the edit flag is true, then open the customiser directly
      if (this.edit) {
        // Make sure the customiser is fully loaded before opening it
        setTimeout(() => {
          this.openCustomiser();
        }, 1000);
      }
    }

    /**
     * FEATURE: Listen to post messages from the customiser
     * Message Types:
     * - 'ready_initialization': Customiser is ready to receive initialization data
     * - 'configuration_saved': User saved their configuration, triggers cart building
     * - 'at_closing': Customiser is closing, closes the Fancybox modal
     * - 'ShareYourCode': User wants to share their configuration code (copies link to clipboard)
     * - 'SendConfigurationEmail': User wants to email their configuration (opens email form)
     */
    customiserMessageHandler() {
      window.addEventListener('message', (e) => {
        try {

          if (!e.origin.includes('precismo') && !e.origin.includes('vercel.app')) {
            return false;
          }

          // guard against different messages
          if (typeof e.data !== 'string' || e.data.indexOf('{') == -1) {
            return false;
          }

          // parse the data
          const data = JSON.parse(e.data);
          const type = data.type.toLowerCase();

          // handle the different messages
          if (type === 'ready_initialization' || type === 'readyinitialization') {
            // handle initialization if not already initialized
            if (this.type == 'auto') {
              this.handleInitialization();
            } else if (this.type == 'moto') {
              this.handleMotoInitialization();
            }
          } else if (type === 'configuration_saved' || type === 'configurationsaved') {
            if (this.type == 'auto') {
              this.handleConfigurationSaved(data);
            } else if (this.type == 'moto') {
              const payload = data.payload;
              const reason = payload.reason; // "share" | "share_link" | "share_email"
              switch (reason) {
                case 'share_email':
                  this.shareConfigurationPopup(payload);
                break;
                case 'share_link': {
                  const url = `${window.location.origin}${window.location.pathname}?configurationCode=${payload.meta.code}`;
                  this.copyToClipboard(url);
                  break;
                }
                case 'share':
                  // no action needed
                break;
              }
            }
          } else if (type === 'add_to_cart' || type === 'addtocart') {
            this.handleConfigurationSaved(data);
          } else if (type === 'configuration_update' || type === 'configurationupdate') {
            this.handleConfigurationUpdated(data);
            if (this.type == 'moto') {
              this.setLocalStorage(data?.payload?.configurationCode);
            }
          } else if (type === 'at_closing' || type === 'atclosing') {
            if (this.type == 'moto') {
              if (data.payload.reason == 'close_without_save' && !this.configurationSaving) {
                this.closeCustomiser();
              }
            } else {
              if (!this.configurationSaving ) {
                this.closeCustomiser();
              }
            }
          } else if (type === 'share_your_code' || type === 'shareyourcode') {
            const code = this.type == 'moto' ? data.payload?.meta?.code : data?.code;
            const url = `${window.location.origin}${window.location.pathname}?configurationCode=${code}`;
            if (this.type == 'moto') {
              this.setLocalStorage(code);
            }
            this.copyToClipboard(url);
          } else if (type == 'send_configuration_email' || type == 'sendconfigurationemail') {
            if (this.type == 'auto') {
              this.shareConfigurationPopup(data);
            } else if (this.type == 'moto') {
              this.setLocalStorage(data?.payload?.meta?.code);
              this.shareConfigurationPopup(data.payload);
            }
          } else if (type === 'error') {
            console.error(`[Configurator] ${data.payload.code}: ${data.payload.message}`);
          }
        } catch (error) {
          console.error(error);
        }
      });
    }

    /**
     * FEATURE: Get the sections to render
     * @returns {Object[]} The sections to render
     */
    getSectionsToRender() {
      return [
        {
          id: 'cart-drawer',
          section: 'cart-drawer',
          selector: '[data-cart-drawer]',
        },
        {
          id: 'cart-icon-bubble',
          section: 'cart-icon-bubble',
          selector: '[data-cart-icon-bubble]',
        },
      ];
    }

    /**
     * FEATURE: Open Customiser Popup
     */
    openCustomiser(event) {
      let that = this;
      if (!this.url) {
        console.error('No customiser url found');
        return;
      }

      new Fancybox(
        [
          {
            src: this.url,
            type: 'iframe',
            trapFocus: false,
            fullscreen: {
              autoStart: true,
            },
          },
        ],
        {
          mainClass: 'product-customiser-modal',
          on: {
            load: (fancybox, eventName) => {
              that.iframeOpen = true;
              that.iframe = document.querySelector('.fancybox__iframe');
              if (!that.initalized) {
                if (that.type == 'auto') {
                  that.handleInitialization();
                }
              }
            },
          },
        }
      );
    }

    /**
     * FEATURE: Close Customiser Popup
     */
    closeCustomiser() {
      this.initalized = false;
      this.iframe = null;
      this.iframeOpen = false;
      Fancybox.close(true);
    }

    /**
     * FEATURE: Get the config code from the URL
     * If the config code is found, it sets the config code input value and returns the config code
     * @returns {string} The config code
     */
    getConfigCodeFromUrl() {
      const urlParams = new URLSearchParams(location.search);
      const configCode = urlParams.get('configurationCode') || '';
      if (configCode) {
        if (this.configCodeInput) {
          this.configCodeInput.value = configCode;
        }
        return configCode;
      }
      if (localStorage.getItem('config_code') && this.type == 'moto') {
        return localStorage.getItem('config_code');
      }
      return '';
    }

    /**
     * FEATURE: Get the edit flag from the URL
     * If the edit flag then the customiser will be opened directly and replace a existing cart item with the new configuration.
     * @returns {boolean} The edit flag
     */
    getEditFlagFromUrl() {
      const urlParams = new URLSearchParams(location.search);
      const edit = urlParams.get('edit') === 'true';
      if (edit) {
        // Remove 'edit' from the URL to prevent conflicts with page refresh
        const url = new URL(window.location.href);
        url.searchParams.delete('edit');
        window.history.replaceState({}, document.title, url.toString());
      }
      return edit;
    }

    /**
     * FEATURE: List to config code changes and update the url and the config code
     * @param {Event} event - The event object
     * @returns {void}
     */
    handleConfigCodeChanges() {
      if (!this.configCodeInput) {
        return;
      }
      this.originalConfigCode = this.configCodeInput.value;
      this.url = `${this.configData.url}?wait_for_initialization=true` + (this.originalConfigCode ? `&configurationCode=${this.originalConfigCode}` : '');
    }

    /**
     * FEATURE: Customiser Initialization
     *
     * Sends initialization data to the external customiser iframe when it first loads.
     * Configures pricing, currency, locale, and customization option prices.
     *
     * Initialization includes:
     * - Base pricing and currency settings
     * - Size adjustment pricing (made-to-measure, standard adjustments)
     * - Add-on item pricing (bootcut cuffs, phone pockets)
     * - Locale for internationalization
     * - Email button visibility setting
     * - Share URL for configuration codes
     */
    handleInitialization() {
      if (this.initalized || this.iframe == null) {
        return;
      }
      this.initalized = true;

      // Build the overrides object
      const overrides = [
        {
          value: parseInt(this.configData.base_price) / 100,
          path: 'custom.alpinestars.pricing.base_price',
        },
        {
          value: window.Shopify.currency.active,
          path: 'custom.alpinestars.pricing.currency',
        },
        {
          value: this.configData.currency_prefix,
          path: 'custom.alpinestars.pricing.currency_prefix',
        },
        {
          value: this.configData.currency_suffix,
          path: 'custom.alpinestars.pricing.currency_suffix',
        },
        {
          value: window.Shopify.locale, // "en", "fr", "de", "es", "it"
          path: 'custom.alpinestars.i8n',
        },
        {
          value: this.configData.add_ons?.non_vector_logo?.price
            ? parseFloat(this.configData.add_ons.non_vector_logo.price) / 100
            : 0,
          path: 'custom.alpinestars.pricing.non_vector_logo',
        },
        {
          path: 'custom.alpinestars.pricing.size',
          value: [
            {
              id: 'made_to_measure',
              value: parseFloat(this.configData.made_to_measure_price) / 100,
            },
            {
              id: 'adjustment',
              value: parseFloat(this.configData.standard_adjustment) / 100,
            },
          ],
        },
        {
          value: location.href,
          path: 'custom.alpinestars.settings.share_url',
        },
        {
          path: 'custom.alpinestars.pricing.items',
          value: [
            {
              config_name: 'leg_cuffs',
              config_option: 'cuffs_standard',
              value: 0,
            },
            {
              config_name: 'leg_cuffs',
              config_option: 'cuffs_boot',
              value:  parseInt(this.configData.cuffs_boot || 0) / 100,
            },
            {
              config_name: 'phone_pocket',
              config_option: 'left_phone_pocket',
              value: this.configData?.add_ons?.phone_pocket?.price
                ? parseInt(this.configData.add_ons.phone_pocket.price) / 100
                : 0,
            },
            {
              config_name: 'phone_pocket',
              config_option: 'right_phone_pocket',
              value: this.configData?.add_ons?.phone_pocket?.price
                ? parseInt(this.configData.add_ons.phone_pocket.price) / 100
                : 0,
            },
            {
              config_name: 'pocket',
              config_option: 'pockets',
              value: this.configData?.add_ons?.pockets?.price
                ? parseInt(this.configData.add_ons.pockets.price) / 100
                : 0,
            },
            {
              config_name: 'belt',
              config_option: 'belt',
              value: this.configData?.add_ons?.belt?.price
                ? parseInt(this.configData.add_ons.belt.price) / 100
                : 0,
            },
          ],
        },
      ];

      // Add to cart behavior
      if (this.configData.add_to_cart_option) {
        overrides.push({
          value: 'true',
          path: 'custom.alpinestars.settings.show_email_button',
        });
      } else {
        overrides.push(
          {
            value: 'E-mail your configuration',
            path: 'custom.alpinestars.pricing.buyButtonText',
          }
        );
      }

      // Build the initialization message
      const defaultCfg = undefined;
      const initializationMessage = JSON.stringify({
        type: 'initialize',
        payload: {
          overrides,
          defaultCfg,
        },
      });

      // Send the initialization message to the customiser
      this.iframe.contentWindow.postMessage(initializationMessage, '*');
    }

    /**
     * FEATURE: Moto Initialization
     */
    handleMotoInitialization() {
      if (this.initalized || this.iframe == null) {
        return;
      }
      this.initalized = true;

      this.basePrice = parseInt(this.configData.base_price) / 100;
      this.madeToMeasurePrice = parseInt(this.configData.made_to_measure_price) / 100;
      this.logoSet1Price = parseInt(this.configData.logo_set_1_price) / 100;
      this.logoSet2Price = parseInt(this.configData.logo_set_2_price) / 100;
      this.logoSet3Price = parseInt(this.configData.logo_set_3_price) / 100;
      this.logoStep2toStep3Price = parseInt(this.logoSet3Price - this.logoSet2Price) / 100;

      const overrides = [
        // Product price
        {
          path: 'ui.texts.price',
          value: `${this.configData.currency_prefix}${ProductCustomiser.formatPrice(this.basePrice, this.configData.language, this.configData.country)}${this.configData.currency_suffix}`,
        },
        // Product title
        {
          path: 'ui.texts.configuratorName',
          value: this.configData.title,
        },
        // Made to measure title
        {
          path: 'ui.texts.customPriceAdition',
          value: this.configData.made_to_measure_title,
        },
        // Logo set 1 price
        {
          path: 'ui.texts.routerSkipPartialPrice',
          value: `+ ${this.configData.currency_prefix}${ProductCustomiser.formatPrice(0, this.configData.language, this.configData.country)}${this.configData.currency_suffix}`,
        },
        // Logo set 2 price
        {
          path: 'ui.texts.routerConfigurePartialPrice',
          value: `+ ${this.configData.currency_prefix}${ProductCustomiser.formatPrice(this.logoSet2Price, this.configData.language, this.configData.country)}${this.configData.currency_suffix}`,
        },
        // Logo set 3 price
        {
          path: 'ui.texts.routerConfigureAllPrice',
          value: `+ ${this.configData.currency_prefix}${ProductCustomiser.formatPrice(this.logoSet3Price, this.configData.language, this.configData.country)}${this.configData.currency_suffix}`,
        },
        // Selected logo set is 2, keep logo set 2 price
        {
          path: 'ui.texts.routerSkipProPrice',
          value: `+ ${this.configData.currency_prefix}${ProductCustomiser.formatPrice(0, this.configData.language, this.configData.country)}${this.configData.currency_suffix}`,
        },
        // Selected logo set is 2, update to 3 price
        {
          path: 'ui.texts.routerConfigureProPrice',
          value: `+ ${this.configData.currency_prefix}${ProductCustomiser.formatPrice(this.logoStep2toStep3Price, this.configData.language, this.configData.country)}${this.configData.currency_suffix}`,
        }
      ];

      // Build the initialization message
      const message = {
        "type": "initialize",
        "payload": {
          lang: window.Shopify.locale,
          overrides
        }
      };

      // Send the message to the customiser
      this.iframe.contentWindow.postMessage(message, '*');
    }

    /**
     * FEATURE AUTO: Variant Selection Logic for Auto Customiser
     * @param {Object} config - The configuration object in Precismo
     * @returns {[string/number, boolean]} The main variant id and made to measure flag
     *
     * Determines the correct Shopify variant ID based on:
     * 1. Size type: made-to-measure vs standard
     * 2. Size adjustments: whether arm/leg adjustments were made
     * 3. Leg cuff style: standard vs bootcut
     *
     * Variant selection hierarchy:
     * - Made-to-measure → uses made_to_measure variants (standard or bootcut)
     * - With adjustments → uses adjustment_sizes variants (standard or bootcut)
     * - Standard → uses base_sizes variants (standard or bootcut)
     */
    getAutoMainVariantId(config) {
      let id = null;
      let madeToMeasure = false;
      if (config.size.id == 'made_to_measure') {
        if (config.leg_cuffs == 'cuffs_standard') {
          id = this.configData.made_to_measure.standard;
          madeToMeasure = true;
        } else {
          id = this.configData.made_to_measure.bootcut;
          madeToMeasure = true;
        }
      } else {
        if (config.size.adjustment.arm != 0 || config.size.adjustment.leg != 0) {
          if (config.leg_cuffs == 'cuffs_standard') {
            id = this.configData.adjustment_sizes[config.size.id];
          } else {
            id = this.configData.adjustment_sizes_bootcut[config.size.id];
          }
        } else {
          if (config.leg_cuffs == 'cuffs_standard') {
            id = this.configData.base_sizes[config.size.id];
          } else {
            id = this.configData.base_sizes_bootcut[config.size.id];
          }
        }
      }
      return [id, madeToMeasure];
    }

    /**
     * FEATURE MOTO: Variant Selection Logic for Moto Customiser
     * @param {Object} config - The configuration object in Precismo
     * @returns {[string/number, boolean]} The main variant id and made to measure flag
    */
    getMotoMainVariant({ size, logoType }) {
      // set a default size if not provided
      if (!size) {
        size = this.configData.variants[0].size;
      }

      // Get the log set id
      const logoSet = ProductCustomiser.#logoTypeMap[logoType] !== undefined ? ProductCustomiser.#logoTypeMap[logoType] : 1;

      // Get the variant id
      let variant = null;
      let madeToMeasure = false;
      if (size == 'mtm' || size == 'made_to_measure') {
        variant = this.configData.variants.find((variant) => variant.logo_set.includes(logoSet) && ['os', 'mtm', 'made to measure'].includes(variant.size.toLowerCase()));
        madeToMeasure = true;
      } else {
        variant = this.configData.variants.find((variant) => variant.size == size && variant.logo_set.includes(logoSet) && !['os', 'mtm', 'made to measure'].includes(variant.size.toLowerCase()));
        madeToMeasure = false;
      }

      // Save the variant id in the state
      this.variant = variant;
      this.madeToMeasure = madeToMeasure;

      // Return the variant id and made to measure flag
      return [variant, madeToMeasure];
    }

    /**
     * FEATURE MOTO: Get main variant id
     */
    getMotoMainVariantId() {
      return [this.variant.id, this.madeToMeasure];
    }

    /**
     * FEATURE AUTO: Get Weight and Height
     *
     * @param {Object} config - The configuration object in Precismo
     * @returns {[number | null, number | null]} The weight and height
     */
    getWeightAndHeight(config) {
      if (!config?.size?.mtm?.inputs || config.size.id != 'made_to_measure') {
        return [null, null];
      }
      let weight = config.size.mtm.inputs.weight || null;
      let height = config.size.mtm.inputs.height || null;
      return [weight, height];
    }

    /**
     * FEATURE: Logo Counting & Classification for Auto Customiser
     *
     * @param {Object} suitConfig - The configuration object in Precismo
     * @returns {[vectorLogo, nonVectorLogo]} The vector and non-vector logo objects
     *
     * Counts and classifies logos into two categories:
     * - Vector logos (SVG, etc.) - charged per logo via logo_product
     * - Non-vector logos (JPG, PNG, etc.) - charged via non_vector_logo_product
     *
     * This distinction is important for pricing as non-vector logos require
     * additional processing and have different pricing.
     */
    getAutoLogoAddons(suitConfig) {
      let vectorLogo = {
        name: 'vector_logo',
        count: 0,
      };
      let nonVectorLogo = {
        name: 'non_vector_logo',
        count: 0,
      };
      suitConfig.forEach((config) => {
        if (config.key.indexOf('logo_') > -1) {
          if (config.value.logos.length > 0) {
            config.value.logos.forEach((logo) => {
              let logoType = logo.type;
              if (
                logoType.indexOf('jpg') > -1 ||
                logoType.indexOf('png') > -1 ||
                logoType.indexOf('jpeg') > -1 ||
                logoType.indexOf('gif') > -1
              ) {
                nonVectorLogo.count++;
              } else {
                vectorLogo.count++;
              }
            });
          }
        }
      });

      // Get the price and cart object for the vector logo
      if (vectorLogo.count > 0 && this.configData?.add_ons?.vector_logo !== undefined) {
        vectorLogo = {
          ...vectorLogo,
          id: this.configData.add_ons.vector_logo.id,
          price: this.configData.add_ons.vector_logo.price,
        };
      }

      // Get the price and cart object for the non-vector logo
      if (nonVectorLogo.count > 0 && this.configData?.add_ons?.non_vector_logo !== undefined) {
        nonVectorLogo = {
          ...nonVectorLogo,
          id: this.configData.add_ons.non_vector_logo.id,
          price: this.configData.add_ons.non_vector_logo.price,
        };
      }

      // Return the vector and non-vector logo objects
      return [vectorLogo, nonVectorLogo];
    }

    /**
     * FEATURE MOTO: Get Logo Type for Moto Customiser
     * @param {Object} config - The configuration object in Precismo
     * @returns {string} The logo type: null, partial, pro
     */
    getMotoLogoType(stats) {
      let tier = 'basic';
      if (stats.pro > 0) tier = 'pro';
      else if (stats.partial > 0) tier = 'partial';
      this.logoType = tier;
      return tier;
    }

     /**
     * FEATURE: Logo Counting & Classification for Moto Customiser
     *
     * @param {Object} stats - The stats object in Precismo
     * @returns {[vectorLogo, nonVectorLogo]} The vector and non-vector logo objects
     *
     * Counts and classifies logos into two categories:
     * - Vector logos (SVG, etc.) - charged per logo via logo_product
     * - Non-vector logos (JPG, PNG, etc.) - charged via non_vector_logo_product
     *
     * This distinction is important for pricing as non-vector logos require
     * additional processing and have different pricing.
     */
     getMotoLogoAddons(stats) {
      // Vector logo is not charged for moto customiser, is included inside the tiers (basic, partial, pro)
      let vectorLogo = {
        name: 'vector_logo',
        count: 0,
      };
      // Non vector logo have extra surcharge above the tiers (basic, partial, pro)
      let nonVectorLogo = {
        name: 'non_vector_logo',
        count: stats.invalidLogos || 0,
      };

      // Get the price and cart object for the non-vector logo
      if (nonVectorLogo.count > 0 && this.configData?.add_ons?.non_vector_logo !== undefined) {
        nonVectorLogo = {
          ...nonVectorLogo,
          id: this.configData.add_ons.non_vector_logo.id,
          price: this.configData.add_ons.non_vector_logo.price,
        };
      }

      // Return the vector and non-vector logo objects
      return [vectorLogo, nonVectorLogo];
    }

    /**
      * FEATURE MOTO: Get size
     *
     * @param {Object} config - The configuration object in Precismo
     * @returns {Object} The partial logo object
     */
    getSize(config) {
      return config['size-selection']?.value || null;
    }


    /**
     * FEATURE: Driver Flag Add-on
     *
     * @param {Object} config - The configuration object in Precismo
     * @returns {Object} The driver flag object
     */
    getDriverFlagAddon(config) {
      let data = {
        name: 'driver_flag',
        count: config.driver_flag ? 1 : 0,
      };
      if (data.count > 0 && this.configData?.add_ons?.driver_flag !== undefined) {
        data = {
          ...data,
          id: this.configData.add_ons.driver_flag.id,
          price: this.configData.add_ons.driver_flag.price,
        };
      }
      return data;
    }

    /**
     * FEATURE: Driver Name Add-on
     *
     * @param {Object} config - The configuration object in Precismo
     * @returns {Object} The driver name object
     */
    getDriverNameAddon(config) {
      let data = {
        name: 'driver_name',
        count: config.driver_name?.name ? 1 : 0,
      };
      if (data.count > 0 && this.configData?.add_ons?.driver_name !== undefined) {
        data = {
          ...data,
          name: 'driver_name',
          id: this.configData.add_ons.driver_name.id,
          price: this.configData.add_ons.driver_name.price,
        };
      }
      return data;
    }

    /**
     * FEATURE: Blood Group Add-on
     *
     * @param {Object} config - The configuration object in Precismo
     * @returns {Object} The blood group object
     */
    getBloodGroupAddon(config) {
      let data = {
        name: 'blood_group',
        count: config.blood_group ? 1 : 0,
      };
      if (data.count > 0 && this.configData?.add_ons?.blood_group !== undefined) {
        data = {
          ...data,
          name: 'blood_group',
          id: this.configData.add_ons.blood_group.id,
          price: this.configData.add_ons.blood_group.price,
        };
      }
      return data;
    }

    /**
     * FEATURE: Pocket Add-on
     *
     * @param {Object} config - The configuration object in Precismo
     * @returns {Object} The pocket object
     */
    getPocketAddon(config) {
      let data = {
        name: 'pocket',
        count: config.pocket === 'no_pockets' ? 0 : 1,
      };
      if (data.count > 0 && this.configData?.add_ons?.pockets !== undefined) {
        data = {
          ...data,
          id: this.configData.add_ons.pockets.id,
          price: this.configData.add_ons.pockets.price,
        };
      }
      return data;
    }

    /**
     * FEATURE: Phone Pocket Add-on
     *
     * @param {Object} config - The configuration object in Precismo
     * @returns {Object} The phone pocket object
     */
    getPhonePocketAddon(config) {
      let data = {
        name: 'phone_pocket',
        count: config.phone_pocket === 'no_phone_pocket' ? 0 : 1,
      };
      if (data.count > 0 && this.configData?.add_ons?.phone_pocket !== undefined) {
        data = {
          ...data,
          id: this.configData.add_ons.phone_pocket.id,
          price: this.configData.add_ons.phone_pocket.price,
        };
      }
      return data;
    }

    /**
     * FEATURE: Belt Add-on
     *
     * @param {Object} config - The configuration object in Precismo
     * @returns {Object} The belt object
     */
    getBeltAddon(config) {
      let data = {
        name: 'belt',
        count: config.belt === 'belt' ? 1 : 0,
      };
      if (data.count > 0 && this.configData?.add_ons?.belt !== undefined) {
        data = {
          ...data,
          id: this.configData.add_ons.belt.id,
          price: this.configData.add_ons.belt.price,
        };
      }
      return data;
    }

    /**
     * FEATURE: Configuration Updated
     *
     * @param {Object} data - The data object
     * @returns {void}
     */
    handleConfigurationUpdated(data) {
      if (this.type != 'moto') {
        return;
      }

      try {

        // inits
        let suitConfig = data.payload.steps;

        // Stats
        this.motoStats = data.payload.stats;

        // Size
        const size = this.getSize(suitConfig);

        // Get logo type: null, partial, pro
        const logoType = this.getMotoLogoType(this.motoStats);

        let [vectorLogo, nonVectorLogo] =
          this.type == 'auto' ? this.getAutoLogoAddons(suitConfig) : this.getMotoLogoAddons(this.motoStats);

        // Get variant
        const [variant, madeToMeasure] = this.getMotoMainVariant({
          size,
          logoType,
        });

        // If the variant is not found, return
        if (!variant || !variant.title) {
          return;
        }

        // Calculate the price
        let price = parseFloat(variant.price);
        if (nonVectorLogo.count > 0) {
          price += ((parseFloat(nonVectorLogo.price) / 100) * parseFloat(nonVectorLogo.count));
        }

        // Update the configurator name and price
        const message = {
          "type": "setConfiguration",
          "payload": {
            "ui.texts.configuratorName": variant.title,
            "ui.texts.price": `${this.configData.currency_prefix}${ProductCustomiser.formatPrice(price, this.configData.language, this.configData.country)}${this.configData.currency_suffix}`,
          }
        };
        this.iframe.contentWindow.postMessage(message, '*');

      } catch (error) {
        console.error(error);
      }
    }

    /**
     * FEATURE: Configuration to Cart Conversion
     *
     * Converts a saved customiser configuration into Shopify cart items.
     * This is the main feature that processes user customizations and builds
     * a cart with the main suit product plus all selected add-ons.
     *
     * Process:
     * 1. Parse configuration data from customiser
     * 2. Count logos (vector vs non-vector) for pricing
     * 3. Determine correct variant based on size type and options
     * 4. Build cart items array with main product + add-ons
     * 5. Add to cart and open cart drawer
     */
    async handleConfigurationSaved(data) {
      if (!this.iframeOpen) {
        return;
      }
      try {

        // Set the configuration saving flag
        this.configurationSaving = true;

        // inits
        let suitConfig = this.type == 'auto' ? data.payload.configuration : data.payload.stepState;

        // Format ConfigData
        let config = this.type == 'auto' ? suitConfig.reduce((acc, config) => {
          acc[config.key] = config.value;
          return acc;
        }, {}) : suitConfig;

        // Get the config id
        let configId = data.payload?.code || data.payload?.meta?.code || null;

        // Get the data from the customiser
        let thumbnails = data.payload.thumbnails.map(t => {
          if (!t.downloadUrl ) {
            return null;
          }
          return t;
        });
        let thumbnail = thumbnails[0]?.downloadUrl || null;

        // Get the main variant id
        let [mainVariantId, madeToMeasure] =
          this.type == 'auto' ? this.getAutoMainVariantId(config) : this.getMotoMainVariantId();
        if (!mainVariantId) {
          throw new Error('No custom suit variant id found');
        }

        // Get the EAN/barcode for the main variant
        let ean = this.configData?.barcodes?.[mainVariantId] ? this.configData?.barcodes?.[mainVariantId].replace('&nbsp;', '').trim() : null;

        // Get weight and height
        let [weight, height] = this.type == 'auto' ? this.getWeightAndHeight(config) : [null, null];

        // Get logo add-ons
        let [vectorLogo, nonVectorLogo] =
          this.type == 'auto' ? this.getAutoLogoAddons(suitConfig) : this.getMotoLogoAddons(this.motoStats);


        // Get add-ons
        let addOns = [];
        if (this.type == 'auto') {
          let driverFlag = this.getDriverFlagAddon(config);
          let driverName = this.getDriverNameAddon(config);
          let bloodGroup = this.getBloodGroupAddon(config);
          let pocket = this.getPocketAddon(config);
          let phonePocket = this.getPhonePocketAddon(config);
          let belt = this.getBeltAddon(config);
          addOns = [vectorLogo, nonVectorLogo, driverFlag, driverName, bloodGroup, pocket, phonePocket, belt];
        } else if (this.type == 'moto') {
          // For moto, only the non vector logo is paid
          addOns = [nonVectorLogo];
        }

        // Add to cart behavior
        if (this.configData.add_to_cart_option) {
          // Remove the existing cart item with the same config id
          this.originalConfigCode && await this.removeCartItem(this.originalConfigCode);

          // Add to cart
          await this.addToCart({
            mainVariantId,
            addOns,
            configId,
            ean,
            thumbnail,
            madeToMeasure,
            weight,
            height,
          });
        } else {
          // Share configuration
          this.shareConfigurationInline(configId, ean, thumbnail, thumbnails, suitConfig);
        }

      } catch (error) {
        // Display the cart error message
        console.error(error);
        const errorElement = document.getElementById('cart-errors');
        if (errorElement && window.cartStrings) {
          errorElement.textContent = window.cartStrings.error;
        }
        document.querySelector('cart-drawer').open();
      } finally {
        // Reset the configuration saving flag
        this.configurationSaving = false;

        // Close the customiser
        this.closeCustomiser();
      }
    }

    /**
     * FEATURE: Remove Cart Item by Config ID
     * @param {string} configId - The configuration code/id
     * @returns {Promise<void>}
     */
    async removeCartItem(configId) {
      if (!configId) {
        return;
      }
      try {
        const cart = await fetch(`${routes.cart_url}`, { ...fetchConfig() });
        const cartData = await cart.json();

        const item = cartData.items.find((item) => item.properties['_suit-config-id'] == configId);
        if (!item) {
          return;
        }

        const updates = {
          [item.key]: 0,
        }

        const response = await fetch(`${routes.cart_update_url}`, {
          ...fetchConfig(),
          ...{ body: JSON.stringify({ updates }) }
        });
        const data = await response.json();

        return;
      } catch (error) {
        console.error(error);
        const errorElement = document.getElementById('cart-errors');
        if (errorElement && window.cartStrings) {
          errorElement.textContent = window.cartStrings.error;
        }
        document.querySelector('cart-drawer').open();
        return;
      }
    }

    /**
     * FEATURE: Add to Cart
     *
     * @param {Object} data - The data object
     * @param {string} data.mainVariantId - The main variant id
     * @param {Object[]} data.addOns - The add-on objects
     * @param {string} data.configId - The configuration code/id
     * @param {string} data.ean - The EAN/barcode for the variant
     * @param {string} data.thumbnail - The thumbnail URL
     * @param {boolean} data.madeToMeasure - Whether the suit is made to measure
     * @param {number} data.weight - The weight
     * @param {number} data.height - The height
     * @returns {Promise<void>}
     */
    async addToCart({ mainVariantId, addOns, configId, ean, thumbnail, madeToMeasure, weight, height }) {
      // Create add to cart data
      let addOnCartData = addOns.filter((addOn) => addOn.count > 0 && addOn.id !== undefined).map((addOn) => ({
        quantity: addOn.count || 1,
        id: addOn.id,
        parent_id: mainVariantId,
        properties: {
          '_suit-base': mainVariantId,
          '_suit-multiplier': addOn.count,
        },
      }));

      let mainAddToCartData = {
        quantity: 1,
        id: parseFloat(mainVariantId),
        properties: {
          '_ean': ean,
          '_suit-thumbnail': thumbnail,
          '_suit-config': true,
          '_suit-config-id': configId,
          '_suit-mtm': madeToMeasure,
          '_suit-weight': weight,
          '_suit-height': height,
          ...addOns.reduce((acc, addOn) => {
            acc[`_suit-${addOn.name}`] = addOn.count;
            if (addOn.price) {
              acc[`_suit-${addOn.name}-price`] = addOn.price;
            }
            return acc;
          }, {}),
        },
      };

      let addToCartData = {
        sections: ['cart-drawer', 'cart-icon-bubble'],
        sections_url: window.location.pathname,
        items: [mainAddToCartData, ...addOnCartData],
      }

      // Add to cart
      try {
        const response = await fetch(`${routes.cart_add_url}`, { ...fetchConfig(), ...{ body: JSON.stringify(addToCartData) } });
        const data = await response.text();
        const jsonData = JSON.parse(data);

        // Pick up cart error
        if (jsonData.errors) {
          const errorElement = document.getElementById('cart-errors');
          if (errorElement) {
            errorElement.textContent = jsonData.errors;
          }
          document.querySelector('cart-drawer').open();
          return;
        }

        // Update cart drawer
        document.querySelector('cart-drawer').renderContents(jsonData);
        document.querySelector('cart-drawer').open();
        this.clearLocalStorage();
      } catch (error) {
        throw new Error(error);
      }
    }

    /**
     * FEATURE: Share Configuration
     *
     * @param {string} configId - The configuration code/id
     * @param {string} ean - The EAN/barcode for the variant
     * @param {string} thumbnail - The thumbnail URL
     * @param {string[]} thumbnails - The thumbnail URLs
     * @param {Object} suitConfig - The suit configuration
     * @returns {void}
     */
    shareConfigurationInline(configId, ean, thumbnail, thumbnails, suitConfig) {
      if (
        !document.querySelector('[data-customiser-load]') ||
        !document.querySelector('[data-customiser-success]') ||
        !document.querySelector('[data-customiser-success-code]') ||
        !document.querySelector('[data-page-customiser-form]')
      ) {
        return;
      }

      this.closeCustomiser();
      document.querySelector('[data-customiser-load]').classList.add('hidden');
      document.querySelector('[data-customiser-success]').classList.remove('hidden');
      document.querySelector('[data-customiser-success] [data-thumbnail]').src = thumbnail;
      document.querySelector('[data-customiser-success-code]').innerHTML = configId;
      document.querySelector('[data-cf-field-type="phone"]').classList.remove('hidden');
      setTimeout(function () {
        document.querySelector('[data-page-customiser-form]').classList.remove('hidden');
      }, 500);

      const myForm = document.querySelector('form[data-cf-form]');
      if (myForm) {
        // Find the first CF form on the page.
        let myForm = document.querySelector('form[data-cf-form]');
        myForm.cfForm.setFieldValue('form_body', JSON.stringify(suitConfig));
        myForm.cfForm.setFieldValue('suit_thumbnail', thumbnail);
        myForm.cfForm.setFieldValue('suit_config_id', configId);
        if (location.href.indexOf('?configCode') > -1) {
          myForm.cfForm.setFieldValue('customiser_url', location.href);
        } else {
          if (configId.length > 0) {
            myForm.cfForm.setFieldValue(
              'customiser_url',
              `${this.configData.url}?configurationCode=${configId}`
            );
          } else {
            myForm.cfForm.setFieldValue('customiser_url', location.href);
          }
        }
        myForm.cfForm.setFieldValue('refferal', 'alpinestars_share_configuration_page');
        myForm.cfForm.setFieldValue('suit_thumbnail_1', thumbnails[0].downloadUrl);
        myForm.cfForm.setFieldValue('suit_thumbnail_2', thumbnails[1].downloadUrl);
        myForm.cfForm.setFieldValue('suit_thumbnail_3', thumbnails[2].downloadUrl);
        myForm.cfForm.setFieldValue('suit_thumbnail_4', thumbnails[3].downloadUrl);
        if (window.alpinestars_refferal) {
          myForm.cfForm.setFieldValue('refferal', window.alpinestars_refferal);
        }
        if (window.alpinestars_refferal_email) {
          myForm.cfForm.setFieldValue('refferal_email', window.alpinestars_refferal_email);
        }
        // myForm.cfForm.beforeSave(() => {
        //   document.querySelector('[data-customiser-success]').classList.add('hidden');
        // });
      }
    }

    /**
     * FEATURE: Share by email
     *
     * Opens the email form modal and pre-populates it with:
     * - Configuration JSON data
     * - Configuration code/ID
     * - Shareable URL with configuration code
     * - Referral information for tracking
     */
    shareConfigurationPopup(data) {
      if (
        !document.querySelector('[data-page-customiser-form]') ||
        !document.querySelector('[data-cf-form]')
      ) {
        return;
      }

      const thumbnail = data?.screenshots?.[0] || data?.thumbnails?.[0]?.downloadUrl || null;
      const code = data?.code || data?.meta?.code || null;

      const myForm = document.querySelector('form[data-cf-form]');
      if (myForm) {
        document.body.classList.add('no-scroll');
        document.querySelector('[data-page-customiser-form]').classList.remove('hidden');
        myForm.cfForm.setFieldValue('form_body', JSON.stringify(data));
        myForm.cfForm.setFieldValue('suit_config_id', code);
        if (thumbnail) {
          document.querySelector('[data-page-customiser-form] [data-thumbnail]').src = thumbnail;
        }
        myForm.cfForm.setFieldValue(
          'customiser_url',
          `${this.configData.url}?configurationCode=${code}`
        );
        myForm.cfForm.setFieldValue('refferal', 'alpinestars_share_configuration');
        myForm.cfForm.setFieldValue('refferal_email', 'eu.help@alpinestars.com');
        document.querySelectorAll('[data-close-form]').forEach(button => {
          button.addEventListener('click', (event) => {
            document.querySelector('[data-page-customiser-form]').classList.add('hidden');
            document.body.classList.remove('no-scroll');
          });
        });
      }
    }

    /**
     * FEATURE: Copy to clipboard
     * @param {string} text - The URL to copy to clipboard
     * @returns {void}
     */
    async copyToClipboard(text) {
      await navigator.clipboard.writeText(text);
      alert(this.configData.code_copied_to_clipboard || 'Your design url has been copied to clipboard');
    }

    /**
     * FEATURE: Set local storage
     * @param {string} code - The configuration code
     * @returns {void}
     */
    setLocalStorage(code) {
      if (!code || this.type == 'auto') {
        return;
      }
      // Only set the local storage if the config code input is not already set
      if (this.configCodeInput && this.configCodeInput.value) {
        return;
      }
      localStorage.setItem('config_code', code);
      this.originalConfigCode = code;
      this.url = `${this.configData.url}?wait_for_initialization=true` + (this.originalConfigCode ? `&configurationCode=${this.originalConfigCode}` : '');
    }

    /**
     * FEATURE: Get local storage
     * @returns {string}
     */
    getLocalStorage() {
      if (this.type == 'auto') {
        return;
      }
      return localStorage.getItem('config_code');
    }

    /**
     * FEATURE: Clear local storage
     * @returns {void}
     */
    clearLocalStorage() {
      if (this.configCodeInput && this.configCodeInput.value) {
        return;
      }
      if (this.type == 'auto') {
        return;
      }
      this.originalConfigCode = '';
      this.url = `${this.configData.url}?wait_for_initialization=true`;
      localStorage.removeItem('config_code');
    }
  }

  window.ProductCustomiser = ProductCustomiser;

  customElements.define('product-customiser', ProductCustomiser);
}
